import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingCircle, RigaPunteggi } from '../components';
import { SquadraTabella, SquadraInSessione, Problema } from '../api/types';
import { sortClassifica } from '../api';

import './TabellaPunteggi.scss'

interface IProps {
  punteggi: number[],
  nomi: { [squadra: string]: string; },
  dettagli_problemi: Problema[],
  classifica: SquadraTabella[]
  squadre: SquadraInSessione[],
  scuole_id: number[],
  soglia: number | null,
  polo?: number
}


type Fields = "p" | "n" | "prob"
type VirtualRow = {
  key: string,
  type: "squadra" | "separator",
  squadra?: SquadraTabella,
  dettagli?: SquadraInSessione,
  evidenzia?: boolean,
  poloInfo?: boolean,
};

const binSearch = (arr: SquadraTabella[], threshold: number) => {
  let left = 0;
  let right = arr.length - 1;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (arr[mid].posizione < threshold) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  return arr[left].posizione;
}


const TabellaPunteggi = (props: IProps) => {
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [sortField, setSortField] = useState<Fields>("p")
  const [sortProb, setSortProb] = useState(0)
  const [mostraScuole, setMostraScuole] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewport, setViewport] = useState({ top: 0, height: 0 });
  const [sizesVersion, setSizesVersion] = useState(0);
  const tbodyRef = useRef<HTMLTableSectionElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const rowHeightsRef = useRef<Record<string, number>>({});
  const rowNodesRef = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const sortFactory = (val: Fields, idx: number = 0) => {
    if (val != "prob") {
      return () => {
        setSortField(val);
        setSortDir(p => (p * -1) as 1 | -1)
      }
    }
    return () => {
      setSortField(val);
      setSortDir(p => (p * -1) as 1 | -1)
      setSortProb(idx)
    }
  }
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const showSeparator = normalizedSearch.length === 0 && sortField === "p" && sortDir === 1 && props.soglia !== null && props.classifica.length > 0;

  const classifica = useMemo(
    () => sortClassifica([...props.classifica], sortDir, sortField, sortProb),
    [props.classifica, sortDir, sortField, sortProb]
  );
  const separatorPos = !showSeparator ? 0 : binSearch(classifica, props.soglia as number);
  const squadreById = useMemo(() => {
    return props.squadre.reduce((acc, sq) => {
      acc[sq.id] = sq;
      return acc;
    }, {} as Record<number, SquadraInSessione>);
  }, [props.squadre]);

  const problema_completo = [];
  for (let i = 0; i < props.dettagli_problemi.length; i++) {
    let cusu: any = Object.assign({}, props.dettagli_problemi[i]);
    cusu.punteggio = (props.punteggi) ? props.punteggi[i] : 0;
    problema_completo.push(cusu);
  }
  const totalColumns = 3 + problema_completo.length;

  const virtualRows = useMemo(() => {
    const rows: VirtualRow[] = [];
    for (const squadra of classifica) {
      const dettagli = squadreById[squadra.id];
      if (normalizedSearch.length > 0) {
        const scuolaSearch = dettagli ? JSON.stringify(dettagli.scuola).toLowerCase() : "";
        const squadraSearch = squadra.nome.toLowerCase();
        if (!squadraSearch.includes(normalizedSearch) && !scuolaSearch.includes(normalizedSearch)) {
          continue;
        }
      }
      const evidenzia = !!dettagli && props.scuole_id.includes(dettagli.scuola.id);
      const poloInfo = dettagli?.polo === props.polo;

      rows.push({
        key: `squadra-${squadra.id}`,
        type: "squadra",
        squadra,
        dettagli,
        evidenzia,
        poloInfo,
      });
      if (showSeparator && separatorPos === squadra.posizione) {
        rows.push({
          key: `separator-${squadra.posizione}`,
          type: "separator",
        });
      }
    }
    return rows;
  }, [classifica, squadreById, props.scuole_id, props.polo, showSeparator, separatorPos, normalizedSearch]);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        top: window.scrollY,
        height: window.innerHeight,
      });
    };

    updateViewport();
    window.addEventListener("scroll", updateViewport, { passive: true });
    window.addEventListener("resize", updateViewport);
    return () => {
      window.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    resizeObserverRef.current = new ResizeObserver((entries) => {
      let hasChanged = false;
      for (const entry of entries) {
        const node = entry.target as HTMLTableRowElement;
        const key = node.dataset.virtualRowKey;
        if (!key) continue;
        const nextHeight = Math.max(1, Math.ceil(entry.contentRect.height));
        if (rowHeightsRef.current[key] !== nextHeight) {
          rowHeightsRef.current[key] = nextHeight;
          hasChanged = true;
        }
      }
      if (hasChanged) {
        setSizesVersion(v => v + 1);
      }
    });
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleFindShortcut = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "f") return;
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };
    document.addEventListener("keydown", handleFindShortcut);
    return () => {
      document.removeEventListener("keydown", handleFindShortcut);
    };
  }, []);

  const setRowRef = useCallback((key: string) => {
    return (node: HTMLTableRowElement | null) => {
      const prev = rowNodesRef.current.get(key);
      if (prev && prev !== node) {
        resizeObserverRef.current?.unobserve(prev);
        rowNodesRef.current.delete(key);
      }
      if (!node) return;
      node.dataset.virtualRowKey = key;
      rowNodesRef.current.set(key, node);
      resizeObserverRef.current?.observe(node);
      const measured = Math.max(1, Math.ceil(node.getBoundingClientRect().height));
      if (rowHeightsRef.current[key] !== measured) {
        rowHeightsRef.current[key] = measured;
        setSizesVersion(v => v + 1);
      }
    };
  }, []);

  const estimatedSquadraHeight = mostraScuole ? 56 : 36;
  const estimatedSeparatorHeight = 8;
  const overscanPx = 300;

  const { topSpacerHeight, bottomSpacerHeight, visibleRows } = useMemo(() => {
    const offsets = [0];
    for (const row of virtualRows) {
      const fallbackHeight = row.type === "separator" ? estimatedSeparatorHeight : estimatedSquadraHeight;
      const measured = rowHeightsRef.current[row.key];
      offsets.push(offsets[offsets.length - 1] + (measured || fallbackHeight));
    }

    const totalHeight = offsets[offsets.length - 1];
    const tbodyRect = tbodyRef.current?.getBoundingClientRect();
    const tbodyTop = (tbodyRect?.top ?? 0) + window.scrollY;
    const visibleTop = Math.max(0, viewport.top - tbodyTop - overscanPx);
    const visibleBottom = Math.min(totalHeight, viewport.top + viewport.height - tbodyTop + overscanPx);

    const lowerBound = (target: number) => {
      let lo = 0;
      let hi = offsets.length - 1;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (offsets[mid] < target) lo = mid + 1;
        else hi = mid;
      }
      return Math.max(0, lo - 1);
    };

    const startIdx = lowerBound(visibleTop);
    const endIdx = Math.min(virtualRows.length, lowerBound(visibleBottom) + 2);

    return {
      topSpacerHeight: offsets[startIdx] || 0,
      bottomSpacerHeight: Math.max(0, totalHeight - (offsets[endIdx] || 0)),
      visibleRows: virtualRows.slice(startIdx, endIdx),
    };
  }, [
    virtualRows,
    viewport.top,
    viewport.height,
    estimatedSquadraHeight,
    sizesVersion,
  ]);

  return (
    <>
      <div className="table-responsive" id="tabella-punteggi">
        <table className="table table-bordered
            table-hover text-nowrap table-responsive-lg">
          <colgroup>
            <col className="col-pos" />
            <col className="col-squadra" />
            <col className="col-punti" />
            {problema_completo.map((_, idx) => <col key={`col-problema-${idx}`} className="col-problema" />)}
          </colgroup>
          <thead className="thead-dark">
            <tr>
              <th rowSpan={2} key="1" onClick={sortFactory("p")}>
                <i className="fa fa-sort" /> Pos
              </th>
              <th rowSpan={2} key="2" onClick={sortFactory("n")}>
                <i className="fa fa-sort" /> Squadra
                <button className="btn btn-info ml-3 " style={{ padding: '0.0rem 0.75rem' }} type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMostraScuole(val => !val)
                  }}
                >
                  {mostraScuole && "Nascondi scuole"}
                  {!mostraScuole && "Mostra scuole"}
                </button>
                <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                  <input
                    ref={searchInputRef}
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="Cerca squadra o scuola..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              </th>
              <th rowSpan={2} key="0">
                Punti
              </th>
              {problema_completo.map(
                (p, idx) => <th className="col-problema" onClick={sortFactory("prob", idx)} key={idx + 3} title={p.nome}>
                  <i className="fa fa-sort" />{`P${p.ordine}`}
                </th>
              )}
            </tr>
            <tr>
              {problema_completo.map(
                (p, idx) => <th className="col-problema" key={idx + 3}>
                  {p.punteggio}
                </th>)}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {topSpacerHeight > 0 && (
              <tr aria-hidden="true">
                <td colSpan={totalColumns} style={{ height: `${topSpacerHeight}px`, padding: 0, border: "none" }} />
              </tr>
            )}

            {visibleRows.map((row) => {

              if (row.type === "separator") {
                return (
                  <tr
                    key={row.key}
                    ref={setRowRef(row.key)}
                    style={{ borderTop: "3px solid #f00" }}
                  >
                    <td colSpan={totalColumns} style={{ padding: 0, border: "none" }} />
                  </tr>
                );
              }

              if (!row.squadra || !row.dettagli) {
                return (
                  <tr key={row.key} ref={setRowRef(row.key)}>
                    <td colSpan={totalColumns}>
                      <LoadingCircle />
                    </td>
                  </tr>
                );
              }

              const poloInfo = props.polo !== undefined ? { polo_info: row.poloInfo } : {};
              return (
                <RigaPunteggi
                  key={row.key}
                  rowRef={setRowRef(row.key)}
                  squadra={row.squadra}
                  nome={props.nomi[row.squadra.nome]}
                  posizione={row.squadra.posizione}
                  evidenzia={!!row.evidenzia}
                  dettagli_squadra={row.dettagli}
                  mostra_scuola={mostraScuole}
                  {...poloInfo}
                />
              );
            })}

            {bottomSpacerHeight > 0 && (
              <tr aria-hidden="true">
                <td colSpan={totalColumns} style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: "none" }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default TabellaPunteggi
